<template>
  <BaseCard
    title="Document Status"
    class="h-full"
  >
    <template #content>
      <div class="flex flex-col gap-4 min-h-fit w-full">
        <div class="flex flex-col gap-4 w-full">
          <div
            v-if="isLoading"
            class="w-full"
          >
            <Skeleton
              height="400px"
              width="100%"
            />
          </div>
          <div
            v-else
            class="flex flex-col gap-4 w-full rounded-lg"
          >
            <ClientOnly>
              <VChart
                class="w-full min-h-[400px] max-h-[500px]"
                :option="option"
                autoresize
              />
              <template #fallback>
                <div class="text-center text-gray-500 p-4">
                  Loading chart...
                </div>
              </template>
            </ClientOnly>
          </div>
        </div>
      </div>
    </template>
  </BaseCard>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
} from 'echarts/components'
import VChart from 'vue-echarts'
import type { ComposeOption } from 'echarts/core'
import type {
  BarSeriesOption,
  TitleComponentOption,
  TooltipComponentOption,
  LegendComponentOption,
  GridComponentOption,
} from 'echarts'
import { useDashboard } from '~/composables/useDashboardCA'

use([
  CanvasRenderer,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
])

  type EChartsOption = ComposeOption<
    | BarSeriesOption
    | TitleComponentOption
    | TooltipComponentOption
    | LegendComponentOption
    | GridComponentOption
  >

interface DocumentStatusData {
  hasAppraisal: number
  hasCIM: number
}

interface BusinessAnalyticsResponse {
  pipelineStatus: unknown[]
  documentStatus: DocumentStatusData
  newBusinessesByMonth: unknown[]
  soldBusinessesByMonth: unknown[]
  businessesByAgent: unknown[]
}

const { getBusinessAnalytics } = useDashboard()

const isLoading = ref(true)
const documentStatus = ref<DocumentStatusData>({
  hasAppraisal: 0,
  hasCIM: 0,
})

const getPrimaryColor = () => {
  return getComputedStyle(document.documentElement)
    ?.getPropertyValue('--p-primary-200')
    ?.trim() || '#97BA9F'
}

const option = computed<EChartsOption>(() => {
  const primaryColor = getPrimaryColor()
  const labels = ['Appraisal', 'CIM']
  const data = [
    documentStatus.value.hasAppraisal,
    documentStatus.value.hasCIM,
  ]

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'transparent',
      textStyle: {
        color: '#fff',
      },
      formatter: (params: unknown) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const param = Array.isArray(params) ? params[0] : params as any
        const value = param?.value
        if (value === null || value === undefined) return ''
        return `${param.name}<br/>${value} businesses`
      },
    },
    grid: {
      left: '3%',
      right: '3%',
      bottom: '10%',
      top: '5%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: {
        fontSize: 12,
        color: '#666',
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: '#e0e0e0',
        },
      },
      axisTick: {
        show: false,
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: '#666',
      },
      splitLine: {
        lineStyle: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
    },
    series: [
      {
        name: 'Businesses',
        type: 'bar',
        data,
        barWidth: '80%',
        itemStyle: {
          color: primaryColor,
          borderRadius: [4, 4, 0, 0],
        },
        label: {
          show: true,
          position: 'top',
          fontSize: 10,
          color: '#333',
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 0,
            shadowOffsetX: 0,
            shadowColor: 'transparent',
          },
        },
      },
    ],
    animation: true,
    animationDuration: 1000,
    animationEasing: 'cubicOut',
  }
})

const getData = async () => {
  isLoading.value = true
  try {
    const response = await getBusinessAnalytics()
    const data = response?.data as BusinessAnalyticsResponse

    if (data) {
      documentStatus.value = data.documentStatus || {
        hasAppraisal: 0,
        hasCIM: 0,
      }
    }
  }
  catch (error) {
    console.error('Error fetching business analytics:', error)
    documentStatus.value = {
      hasAppraisal: 0,
      hasCIM: 0,
    }
  }
  finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  await getData()
})
</script>
